import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock } from "lucide-react";
import { getEvents } from "@/lib/api";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const CALENDAR = [
  { name: "October 2026", start: 4, days: 31, marks: {} },
  { name: "November 2026", start: 0, days: 30, marks: { 3: "GENERAL" } },
];

function CalendarCard({ month }) {
  const cells = [];
  for (let i = 0; i < month.start; i++) cells.push(null);
  for (let d = 1; d <= month.days; d++) cells.push(d);
  return (
    <div className="border border-navy/15 bg-white rounded-sm p-5 md:p-6" data-testid={`calendar-${month.name.split(" ")[0].toLowerCase()}`}>
      <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-navy mb-4">{month.name}</p>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-steel font-semibold mb-2">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          const mark = d ? month.marks[d] : null;
          return (
            <div key={i} className="aspect-square flex items-center justify-center">
              {d && (
                <span
                  title={mark || undefined}
                  className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full text-sm ${
                    mark === "GENERAL"
                      ? "bg-gold text-navy font-black"
                      : mark
                        ? "bg-navy text-white font-bold"
                        : "text-navy/50"
                  }`}
                  data-testid={mark ? `cal-day-${month.name.split(" ")[0].toLowerCase()}-${d}` : undefined}
                >
                  {d}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    getEvents()
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]));
  }, []);

  return (
    <section id="events" className="bg-[#F8F9FA] py-24 md:py-36 px-5 md:px-10" data-testid="events-section">
      <div className="max-w-6xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-xs uppercase tracking-[0.3em] font-semibold text-electric mb-6"
        >
          On The Trail
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-navy mb-14 md:mb-20"
        >
          Come meet David.
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="grid sm:grid-cols-2 gap-5 md:gap-8 mb-14 md:mb-20 max-w-3xl"
          data-testid="trail-calendar"
        >
          {CALENDAR.map((m) => (
            <CalendarCard key={m.name} month={m} />
          ))}
        </motion.div>

        {events.length === 0 ? (
          <div className="border-t border-navy/15 pt-10 md:pt-14" data-testid="events-empty">
            <p className="font-serif italic text-lg md:text-xl text-navy/60 max-w-xl leading-relaxed">
              New campaign trail dates for the general election are being finalized — check back soon,
              or call Dave directly at 421-0970.
            </p>
          </div>
        ) : (
        <div className="border-t border-navy/15">
          {events.map((e, i) => {
            const d = new Date(`${e.date}T12:00:00`);
            return (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                className="group grid grid-cols-[auto_1fr] md:grid-cols-[120px_1fr_auto] gap-5 md:gap-10 py-8 md:py-10 border-b border-navy/15 items-center hover:bg-navy/[0.03] transition-colors duration-300 px-2 md:px-4"
                data-testid={`event-row-${i}`}
              >
                <div className="text-center md:text-left">
                  <span className="block text-[10px] uppercase tracking-[0.3em] font-bold text-electric">
                    {MONTHS[d.getMonth()]}
                  </span>
                  <span className="block font-serif text-4xl md:text-5xl font-black text-navy leading-none">
                    {d.getDate()}
                  </span>
                </div>
                <div>
                  <h3 className="font-sans text-lg md:text-2xl font-bold tracking-tight text-navy group-hover:text-electric transition-colors duration-300">
                    {e.title}
                  </h3>
                  <p className="text-sm md:text-base text-navy/60 mt-2 leading-relaxed max-w-2xl">{e.description}</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs md:text-sm text-steel">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-gold" /> {e.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-gold" /> {e.time}
                    </span>
                  </div>
                </div>
                <span className="hidden md:block font-serif italic text-lg text-navy/30 group-hover:text-gold transition-colors duration-500">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </motion.div>
            );
          })}
        </div>
        )}
      </div>
    </section>
  );
}
